import type { FC } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { Point } from "highcharts";
import { union } from "lodash";
import ReactDOMServer from "react-dom/server";
import styled, { createGlobalStyle } from "styled-components";

import { i18nFilter, translate } from "app/filters/i18nFilter";
import { abbrNumberFilter, abbrNumberSupportNegativeFilter, yAxisChangeFilter } from "app/filters/ngFilters";
import { getYaxisFormat } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelAnalysisChart/ChannelAnalysisChartConfigHelpers";
import { trafficSources as trafficSourcesDefinitons } from "app/Shared/utils";
import Chart from "pro-features/components/Chart/src/Chart";
import combineConfigs from "pro-features/components/Chart/src/combineConfigs";
import noLegendConfig from "pro-features/components/Chart/src/configs/legend/noLegendConfig";
import xAxisCrosshair from "pro-features/components/Chart/src/configs/xAxis/xAxisCrosshair";
import xAxisLabelsConfig from "pro-features/components/Chart/src/configs/xAxis/xAxisLabelsConfig";
import yAxisLabelsConfig from "pro-features/components/Chart/src/configs/yAxis/yAxisLabelsConfig";

import { PoPCompareChangeTooltip } from "pro-features/components/PoPCompareChangeTooltip/PoPCompareChangeTooltip";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { tooltipPositioner } from "app/pages/website-analysis/traffic-sources/mmx/components/popCompareTooltipPositioner";
import type { IPoPGraphOptions } from "pro-features/components/Chart/src/components/PeriodOverPeriodChart/PoPChartTypes";
import tooltipContainerConfig from "pro-features/components/Chart/src/configs/tooltip/tooltipContainerConfig";
import { isRemoveSplitPopCompare } from "app/pages/website-analysis/traffic-sources/mmx/components/common/MobileSplit-utils";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import ChannelsOverviewBarChartLegend from "../../ChannelsOverviewBarChart/components/channels-overview-bar-chart-legend/ChannelsOverviewBarChartLegend";
import type { IChartRawData } from "../types";

import type { ISeries } from "./types";
import { getLegendData } from "./utils";
import { allTrackers } from "app/services/track/track";

interface IPoPCompareChart {
    chartRawData: IChartRawData;
    chosenSites: Array<string>;
    getSiteColor: (site: string) => string;
    durations: Array<string>;
    options?: IPoPGraphOptions;
    globalParams?: { from; compareFrom; params };
    isPercentageDataMode: boolean;
}

const ChartLegendContainer = styled.div`
    padding: 24px;
`;
ChartLegendContainer.displayName = "ChartLegendContainer";

const ChartContainer = styled.div`
    padding: 16px 24px 16px 16px;
    flex: 1;
`;
ChartContainer.displayName = "ChartLegendContainer";

const GlobalStyle = createGlobalStyle`
  .highcharts-tooltip-container{
    z-index: 9999 !important;
  }
`;

const ChartAndLegendWrapper = styled(FlexRow)`
    margin-top: 32px;
    min-height: 313px;
`;

const MAX_CHANGE_PERCENTAGE = 50;

interface IPoPChangeTooltipContainerProps {
    isPercentageDataMode: boolean;
    pointsData: Array<Point>;
    durations: Array<string>;
    translateSourceText: (source: string) => string;
}

const PoPChangeTooltipContainer: FC<IPoPChangeTooltipContainerProps> = (props) => {
    const { isPercentageDataMode, pointsData, durations, translateSourceText } = props;
    const tooltipFormatter = ({ value }) => getYaxisFormat("TrafficShare", value, false);
    // @ts-expect-error FIXME: Argument of type 'number' is not assignable to parameter of type 'boolean'
    const getAbbrNumberFilter = () => (val) => abbrNumberFilter()(val, 2);

    return (
        <PoPCompareChangeTooltip
            headersList={[
                translate("common.tooltip.domain"),
                durations[0],
                durations[1],
                translate(
                    isPercentageDataMode
                        ? "common.tooltip.change"
                        : "website_analysis.marketing_channels.channels.overview.tooltip.abs.change.title",
                ),
            ]}
            title={`${translateSourceText(String(pointsData[0].key))} traffic`}
            pointsData={pointsData}
            yFormatter={tooltipFormatter}
            changeFormatter={isPercentageDataMode ? yAxisChangeFilter : getAbbrNumberFilter}
            cellWidth={100}
        />
    );
};

export const PoPCompareChart: FC<IPoPCompareChart> = ({
    chartRawData,
    chosenSites,
    getSiteColor,
    durations,
    globalParams,
    isPercentageDataMode,
}) => {
    const [hiddenLegends, setHiddenLegends] = useState([]);
    const { from, compareFrom, params } = globalParams;
    const translateSourceText = (source) => {
        return i18nFilter()(trafficSourcesDefinitons?.[source]?.title ?? source);
    };

    const extractPoPCompareDataPerWebsite = (data: IChartRawData, website: string) => {
        const regularData = data.Data[website] ?? null;
        const comparedData = data.ComparedData[website] ?? null;

        if (!regularData || !comparedData) {
            return null;
        }

        // sorting and applying translations for bars
        const channels = union(Object.keys(regularData), Object.keys(comparedData))
            .filter((channel) => {
                return Object.prototype.hasOwnProperty.call(trafficSourcesDefinitons, channel);
            })
            .sort((ch1, ch2) => {
                return trafficSourcesDefinitons[ch1].priority - trafficSourcesDefinitons[ch2].priority;
            })
            .map((channel) => ({ key: channel, name: translateSourceText(channel) }));

        const calculateChange = (regular, compared) => {
            if (isPercentageDataMode) {
                if (compared === 0) {
                    return [(regular + 5000) / (compared + 5000) - 1]; // represents Infinite change
                }
                const change = regular / compared - 1;
                return change > MAX_CHANGE_PERCENTAGE ? [MAX_CHANGE_PERCENTAGE] : [change];
            } else {
                const change = regular - compared;
                return [change];
            }
        };

        return channels.reduce((acc, { key, name }) => {
            const regular = regularData[key] ?? 0;
            const compared = comparedData[key] ?? 0;
            acc.push({
                Values: [
                    { Key: name, Value: regular },
                    { Key: name, Value: compared },
                ],
                Change: calculateChange(regular, compared),
            });
            return acc;
        }, []);
    };

    const parseChartRawData = (
        chartRawData: IChartRawData,
        chosenSites: Array<string>,
        from: string,
        compareFrom: string,
    ): ISeries[] => {
        if (params.webSource === devicesTypes.MOBILE) {
            chartRawData = isRemoveSplitPopCompare(from, compareFrom, chartRawData, chosenSites);
        }
        const allData = {};
        chosenSites.map((website) => {
            allData[website] = extractPoPCompareDataPerWebsite(chartRawData, website);
        });
        return adaptCompareData(allData);
    };

    const adaptCompareData = (dataSeries): ISeries[] => {
        const series = [];
        Object.keys(dataSeries).map((website) => {
            const channels = dataSeries[website] ?? [];
            series.push({
                name: website,
                visible: hiddenLegends.find((legend) => legend.name === website)?.visible ?? true,
                color: getSiteColor(website),
                data: channels.map((channel) => ({
                    y: channel && channel.Change ? channel?.Change[0] : null,
                    name: channel.Values[0].Key ?? null,
                    values: channel.Values,
                })),
            });
        });
        return series;
    };

    const chartData = parseChartRawData(chartRawData, chosenSites, from, compareFrom);

    const getLegendItems = () => {
        return chartData.map((item) => {
            return {
                name: item.name,
                visible: item.visible,
                color: item.color,
                data: getLegendData(item.data, isPercentageDataMode),
            };
        });
    };

    ///////////// Chart Configuration //////////////////

    const yAxisFormatter = ({ value }) =>
        isPercentageDataMode ? yAxisChangeFilter()(value, 0, 50) : abbrNumberSupportNegativeFilter()(value);

    const defaultXAxisFormatter = ({ value }) => value;

    const getMaxYAxisValue = () => {
        const maxValue = Math.max(
            ...chartData.filter((item) => item.visible).flatMap((item) => item.data.map((data) => data.y)),
        );
        return maxValue >= MAX_CHANGE_PERCENTAGE ? MAX_CHANGE_PERCENTAGE : maxValue;
    };

    const getMinYAxisValue = () => {
        const minValue = Math.min(
            ...chartData.filter((item) => item.visible).flatMap((item) => item.data.map((data) => data.y)),
        );
        return minValue > 0 ? 0 : minValue;
    };

    const minMaxYAxisValue = isPercentageDataMode
        ? {
              min: getMinYAxisValue(),
              max: getMaxYAxisValue(),
          }
        : {};

    const chartConfig = combineConfigs({ yAxisFormatter, xAxisFormatter: defaultXAxisFormatter }, [
        yAxisLabelsConfig,
        xAxisLabelsConfig,
        noLegendConfig,
        tooltipContainerConfig,
        xAxisCrosshair,
        {
            chart: {
                zooming: {
                    type: null,
                },
                height: 297,
            },
            yAxis: {
                ...minMaxYAxisValue,
            },
            xAxis: {
                type: "category",
                crosshair: true,
                labels: {
                    style: {
                        fontSize: "14px",
                        textAlign: "center",
                        fontFamily: '"Roboto", sans-serif',
                    },
                },
            },
            tooltip: {
                followPointer: false,
                shared: true,
                outside: true,
                useHTML: true,
                positioner: tooltipPositioner,
                formatter: function () {
                    return ReactDOMServer.renderToString(
                        <PoPChangeTooltipContainer
                            pointsData={this.points}
                            durations={durations}
                            translateSourceText={translateSourceText}
                            isPercentageDataMode={isPercentageDataMode}
                        />,
                    );
                },
            },
            plotOptions: {
                column: {
                    marker: {
                        enabled: false,
                    },
                },
            },
        },
    ]);

    const onLegendClick = (selectedLegend) => {
        const action = selectedLegend.hidden ? "add" : "remove";
        const currentHiddenLegendsArr = getLegendItems().map((legend) => {
            if (legend.name === selectedLegend.name) {
                legend.visible = !legend.visible;
            }
            return legend;
        });
        setHiddenLegends(currentHiddenLegendsArr);
        allTrackers.dispatchTrackEvent(
            "Graph Filter",
            "click",
            `Bar Chart/Traffic Sources Overview/${selectedLegend.name || ""}/${action || ""}`,
        );
    };

    const [chartKey, setChartKey] = useState(() => Math.random());
    const isMountedRef = useRef(false);
    useEffect(() => {
        if (isMountedRef.current) {
            setChartKey(Math.random());
        }
        isMountedRef.current = true;
        // depend on the raw prop and the percentage/absolute toggle, not the derived
        // `chartData` array, which is a new reference on every render and would
        // otherwise remount the chart in a loop. Highcharts doesn't fully reset
        // previously-applied yAxis min/max (and tooltip/positioner state) via
        // `update()` when switching between percentage and absolute scales, so a
        // full remount is needed on that toggle too.
    }, [chartRawData, isPercentageDataMode]);

    return (
        <ChartAndLegendWrapper>
            <ChartContainer key={chartKey}>
                <div className="chartContainer">
                    {/*fix SIM-34274*/}
                    <GlobalStyle />
                    <Chart
                        type="column"
                        data={chartData}
                        config={chartConfig}
                    />
                </div>
            </ChartContainer>
            <ChartLegendContainer>
                <FlexRow>
                    <ChannelsOverviewBarChartLegend
                        legendItems={getLegendItems()}
                        onLegendsChange={onLegendClick}
                        titles={[translate("common.domain"), translate("common.tooltip.change")]}
                    />
                </FlexRow>
            </ChartLegendContainer>
        </ChartAndLegendWrapper>
    );
};
